"use client";
/*
 * Documentation:
 * Avatar — https://app.subframe.com/06b570ce924b/library?component=Avatar_bec25ae6-5010-4485-b46b-cf79e3943ab2
 */

import React from "react";
import * as SubframeUtils from "../utils";

interface AvatarRootProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?:
    | "brand"
    | "neutral"
    | "brand-alt"
    | "rose"
    | "rose-alt"
    | "beige"
    | "beige-alt"
    | "neutral-alt-2";
  size?: "x-large" | "large" | "medium" | "small" | "x-small";
  children?: React.ReactNode;
  image?: string;
  square?: boolean;
  className?: string;
}

const AvatarRoot = React.forwardRef<HTMLDivElement, AvatarRootProps>(
  function AvatarRoot(
    {
      variant = "brand",
      size = "medium",
      children,
      image,
      square = false,
      className,
      ...otherProps
    }: AvatarRootProps,
    ref
  ) {
    return (
      <div
        className={SubframeUtils.twClassNames(
          "group/bec25ae6 flex h-8 w-8 flex-col items-center justify-center gap-2 overflow-hidden rounded-full bg-primary-navy-100 relative",
          {
            "rounded-md": square,
            "h-5 w-5": size === "x-small",
            "h-6 w-6": size === "small",
            "h-12 w-12": size === "large",
            "h-16 w-16": size === "x-large",
            "bg-neutral-700": variant === "neutral-alt-2",
            "bg-secondary-beige-500": variant === "beige-alt",
            "bg-secondary-beige-200": variant === "beige",
            "bg-primary-rose-alternate": variant === "rose-alt",
            "bg-primary-rose": variant === "rose",
            "bg-primary-navy-600": variant === "brand-alt",
            "bg-neutral-200": variant === "neutral",
          },
          className
        )}
        ref={ref}
        {...otherProps}
      >
        {children ? (
          <span
            className={SubframeUtils.twClassNames(
              "line-clamp-1 w-full font-['Manrope'] text-[14px] font-[600] leading-[14px] text-primary-navy-800 text-center absolute",
              {
                "font-['Inter'] text-[10px] font-[500] leading-[10px] tracking-normal":
                  size === "x-small" || size === "small",
                "font-['Inter'] text-[18px] font-[500] leading-[18px] tracking-normal":
                  size === "large",
                "font-['Manrope'] text-[24px] font-[500] leading-[24px] tracking-normal":
                  size === "x-large",
                "text-neutral-50": variant === "neutral-alt-2",
                "text-secondary-beige-200": variant === "beige-alt",
                "text-secondary-beige-600": variant === "beige",
                "text-primary-dusty-rose-200": variant === "rose-alt",
                "text-primary-dusty-rose-800": variant === "rose",
                "text-primary-navy-100": variant === "brand-alt",
                "text-neutral-800": variant === "neutral",
              }
            )}
          >
            {children}
          </span>
        ) : null}
        {image ? (
          <img
            className={SubframeUtils.twClassNames(
              "hidden h-8 w-8 flex-none object-cover absolute",
              {
                "h-5 w-5 flex-none": size === "x-small",
                "h-6 w-6 flex-none": size === "small",
                "h-12 w-12 flex-none": size === "large",
                "h-16 w-16 flex-none": size === "x-large",
              }
            )}
            src={image}
          />
        ) : null}
      </div>
    );
  }
);

export const Avatar = AvatarRoot;
